import React, { useEffect, useRef } from 'react'
import { posthog } from '@chilipiper/config'
import { PostHogProvider, usePostHog } from 'posthog-js/react'
import { useFireSessionContext } from '@chilipiper/fire-session'

export const UserPosthogProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <PostHogProvider
      apiKey={posthog.apiKey}
      options={{
        api_host: posthog.apiHost,
        ui_host: posthog.uiHost,
        defaults: '2025-05-24',
        person_profiles: 'identified_only',
        capture_exceptions: true,
        debug: false,
      }}
    >
      <PostHogIdentityUser>{children}</PostHogIdentityUser>
    </PostHogProvider>
  )
}

export const GuestPosthogProvider = ({
  children,
  guestId,
  tenantId,
}: {
  children: React.ReactNode
  guestId: string
  tenantId: string
}) => {
  return (
    <PostHogProvider
      apiKey={posthog.apiKey}
      options={{
        api_host: posthog.apiHost,
        ui_host: posthog.uiHost,
        defaults: '2025-05-24',
        person_profiles: 'identified_only',
        capture_exceptions: true,
        debug: false,
      }}
    >
      <PostHogIdentityGuest guestId={guestId} tenantId={tenantId}>
        {children}
      </PostHogIdentityGuest>
    </PostHogProvider>
  )
}

const PostHogIdentityUser = ({ children }: { children: React.ReactNode }) => {
  const posthog = usePostHog()
  const { userId, tenantId, user } = useFireSessionContext()
  const hasIdentified = useRef(false)

  useEffect(() => {
    if (hasIdentified.current || !userId || !posthog) {
      return
    }

    hasIdentified.current = true

    posthog.identify(userId, {
      email: user.email,
      name: user.name,
      tenantId: tenantId,
      isSuperAdmin: user.isSuperAdmin,
    })

    posthog.group('tenant', tenantId)
  }, [userId, tenantId, user.email, user.name, user.isSuperAdmin, posthog])

  return <>{children}</>
}

const PostHogIdentityGuest = ({
  children,
  guestId,
  tenantId,
}: {
  children: React.ReactNode
  guestId: string
  tenantId: string
}) => {
  const posthog = usePostHog()
  const hasIdentified = useRef(false)

  useEffect(() => {
    if (hasIdentified.current || !posthog || !guestId) {
      return
    }

    hasIdentified.current = true

    posthog.identify(guestId, {
      tenantId: tenantId,
    })
    posthog.group('tenant', tenantId)
  }, [guestId, posthog, tenantId])

  return <>{children}</>
}
